// Explicit pairs from the design cheatsheet (desktop → mobile)
const FONT_SIZE_MAP: Record<number, number> = {
  12: 12,
  14: 14,
  16: 16,
  18: 16,
  20: 18,
  22: 18,
  24: 20,
  26: 20,
  28: 22,
  30: 22,
  32: 24,
  34: 24,
  36: 26,
  38: 26,
  40: 28,
  42: 30,
  44: 30,
  46: 32,
  48: 32,
  52: 34,
  56: 38,
  60: 40,
  64: 42,
  68: 44,
  72: 46,
  76: 48,
  80: 50,
  84: 54,
  88: 56,
  92: 58,
  96: 60,
};

// Fallback formula for sizes not in the table (matches table within ±1px)
export function getMobileFontSize(desktop: number): number {
  if (FONT_SIZE_MAP[desktop] !== undefined) return FONT_SIZE_MAP[desktop];
  if (desktop <= 14) return desktop;
  return Math.round(14 + (desktop - 14) * 0.555);
}

/**
 * Returns a `clamp(mobile, calc(...), desktop)` that linearly interpolates
 * between a 320px viewport (mobile size) and a 1440px viewport (desktop size).
 * If `mobilePx` is omitted, it is derived via `getMobileFontSize`.
 */
export function fluidClamp(desktopPx: number, mobilePx?: number): string {
  const mobile = mobilePx ?? getMobileFontSize(desktopPx);
  const slope = (desktopPx - mobile) / 1120;
  const intercept = mobile - slope * 320;
  return `clamp(${mobile}px, calc(${intercept.toFixed(4)}px + ${(slope * 100).toFixed(4)}vw), ${desktopPx}px)`;
}

/**
 * Declared pixel size of a typography CSS variable — `18px`, or the desktop
 * `max` of the `clamp()` this module rewrites it into. This is the number the
 * design system panel shows, which is what "Default" in the font-size dropdown
 * has to match.
 */
export function designSystemFontSizePx(cssVar: string): number | null {
  const raw = getComputedStyle(document.documentElement)
    .getPropertyValue(cssVar)
    .trim();
  return parsePx(raw) ?? parseClampMaxPx(raw);
}

function parsePx(value: string): number | null {
  const match = value.trim().match(/^(\d+(?:\.\d+)?)px$/);
  return match ? parseFloat(match[1]) : null;
}

// Parse the `max` argument out of a `clamp(min, preferred, max)` string, so
// that a second `applyFluidTypography()` call can still recover the original
// desktop px even after the first call rewrote the CSS var.
function parseClampMaxPx(value: string): number | null {
  const match = value
    .trim()
    .match(/clamp\s*\([^,]+,[^,]+,\s*(\d+(?:\.\d+)?)px\s*\)$/);
  return match ? parseFloat(match[1]) : null;
}

const FLUID_CSS_VARS = [
  "--display-size",
  "--title-size",
  "--heading-size",
  "--subtitle-size",
  "--paragraph-size",
] as const;

export function applyFluidTypography(): void {
  if (document.body.classList.contains("printToPdf")) return;

  const root = document.documentElement;
  const style = getComputedStyle(root);

  for (const cssVar of FLUID_CSS_VARS) {
    const rawValue = style.getPropertyValue(cssVar).trim();
    const desktopPx = parsePx(rawValue) ?? parseClampMaxPx(rawValue);
    if (desktopPx === null) continue;

    root.style.setProperty(cssVar, fluidClamp(desktopPx));
  }
}
